/**
 * **预设窗口布局** —— 像 Blender 的 workspace 标签那样，一键换一整套排布。
 *
 * ## 为什么换成预设，而不是「切编辑器」的模式栏
 *
 * 上一版手机档底部有一条模式栏（节点图 / 属性 / 输出），点一下
 * `revealEditor()` 切过去并整屏。用户实测后要求：
 *
 * > 去掉移动端底部的按钮，让用户自己用多窗口切换需要的窗口，
 * > 并且我们可以预设一些窗口布局，就像 blender 一样有很多组合的布局，
 * > 切换自动就有对应的窗口。
 *
 * 差别是**控制的对象不同**：
 *   · 模式栏控制「当前显示哪个编辑器」—— 一次只能一个，且底部常驻一条 UI；
 *   · 预设控制「整个工作区怎么排」—— 可以是 1 个、2 个窗口，
 *     而且**不占常驻 UI**（放在「窗口」菜单里）。
 *
 * 后者更接近 Blender，也确实更适合「我要一边看节点图一边看输出」这种需求。
 *
 * ## ⚠️ id 必须写死，不能让 `nextId()` 生成
 *
 * 预设会被反复套用。如果用自增 id，每切换一次 id 就变一次：
 *   · Vue 会认为整棵树都换了 → 所有 Area **卸载重建** →
 *     里面的 VueFlow 视口、选中、折叠状态全丢（spike 里守的就是这个）；
 *   · `activePresetId` 也没法靠 id 比对来打勾。
 * 所以每个预设用固定的 id，**同一个预设每次套用都得到同一棵树**。
 */
import { EDITOR_META, editorOf } from './areas'
import type { AreaLeaf, AreaNode, EditorType, LayoutState, RegionKey } from './areas'

function area(id: string, editor: EditorType, off: readonly RegionKey[] = []): AreaLeaf {
  const allowed = new Set(EDITOR_META[editor].regions)
  const regions = Object.fromEntries(
    (['header', 'minimap'] as RegionKey[]).map((key) => [
      key,
      allowed.has(key) && !off.includes(key),
    ]),
  ) as AreaLeaf['regions']
  /* ⚠️ 预设给的窗口**本来就是**这个编辑器：当前和默认一起设（见 AreaLeaf.defaultEditor） */
  return { kind: 'area', id, editor, defaultEditor: editor, regions }
}

/** 左右（`row`）或上下（`col`）两栏 */
function split(
  dir: 'row' | 'col',
  ratio: number,
  id: string,
  a: AreaNode,
  b: AreaNode,
): AreaNode {
  return { kind: 'split', id, dir, ratio, a, b }
}

function layout(root: AreaNode, focused: string): LayoutState {
  return { root, maximized: null, focused }
}

export interface LayoutPreset {
  id: string
  label: string
  /** 一句话说明这份布局适合干什么 */
  hint: string
  build: () => LayoutState
}

/**
 * 预设清单。顺序就是菜单里的顺序。
 *
 * ⚠️ **上下结构（`col`）是手机上的主力**：390px 宽切左右两栏之后
 * 每栏不到 200px，节点图基本没法看；上下切则是「宽度不变、高度减半」，
 * 手机上仍然能用。所以「节点图 + 某个面板」这几份都用 `col`，
 * 只留一份 `row` 给平板 / 横屏。
 */
export const LAYOUT_PRESETS: readonly LayoutPreset[] = [
  /*
   * ★ **文档（首页）** —— 2027 加，排在最前面。
   *
   * 用户原话：「我们需要一个新的窗口，类似于管理页面**也可以用于首页**」。
   * 按他选的那条，这个窗口**不自动开**（默认布局一个字节不动）；
   * 但"当首页用"这个诉求得有个**一键**的落点 —— 想当首页的人点一下就有，
   * 不想的人当它不存在。
   */
  {
    id: 'documents',
    label: '文档（首页）',
    hint: '单窗口；库里的文档：打开 / 改名 / 导入导出 —— **默认就是这一份**',
    build: () => layout(area('p-docs', 'documents'), 'p-docs'),
  },
  {
    /*
     * ★★ **默认布局就是这一份**（用户 2027 要的："文档窗口做成默认窗口"）。
     *
     * ⚠️ 为什么不是"只有文档窗口"那一份：打开一份文档之后**得有地方看**。
     * 单窗口的话，"打开"这个动作在界面上什么都不会发生（文档装进了画布，
     * 而画布没有窗口）—— 那是比"看不见文档列表"更糟的体验。
     * 上下分栏（`col`）而不是左右：手机上宽度是稀缺的，见文件头那段。
     */
    id: 'documents-graph',
    label: '文档 + 节点图',
    hint: '上面文档库、下面画布；一边挑一边看',
    build: () =>
      layout(
        split('col', 0.42, 'p-dg', area('p-docs', 'documents'), area('p-graph', 'graph')),
        'p-docs',
      ),
  },
  {
    id: 'graph',
    label: '只要节点图',
    hint: '单窗口，画布占满',
    build: () => layout(area('p-graph', 'graph'), 'p-graph'),
  },
  {
    id: 'graph-console',
    label: '节点图 + 输出',
    hint: '上下分栏；下半实时显示诊断与校验',
    build: () =>
      layout(
        split('col', 0.62, 'p-gc', area('p-graph', 'graph'), area('p-console', 'console')),
        'p-graph',
      ),
  },
  {
    id: 'graph-properties',
    label: '节点图 + 属性',
    hint: '上下分栏；下半看模板与选中节点的属性',
    build: () =>
      layout(
        split(
          'col',
          0.62,
          'p-gp',
          area('p-graph', 'graph'),
          area('p-properties', 'properties'),
        ),
        'p-graph',
      ),
  },
  {
    id: 'graph-console-properties',
    label: '节点图 + 输出 + 属性',
    hint: '三栏（上下）：画布 / 输出 / 属性',
    build: () =>
      layout(
        split(
          'col',
          0.5,
          'p-gcp',
          area('p-graph', 'graph'),
          split(
            'col',
            0.5,
            'p-cp',
            area('p-console', 'console'),
            area('p-properties', 'properties'),
          ),
        ),
        'p-graph',
      ),
  },
  {
    id: 'graph|console',
    label: '节点图 | 输出',
    hint: '左右分栏；适合平板 / 横屏',
    build: () =>
      layout(
        split('row', 0.68, 'p-g|c', area('p-graph', 'graph'), area('p-console', 'console')),
        'p-graph',
      ),
  },
  {
    id: 'graph-theme',
    label: '节点图 + 主题',
    hint: '左右分栏；改主题时直接看到画布上的变化',
    /*
     * ⚠️ 这一份是**「主题编辑器是窗口而不是浮层」的兑现**。
     *
     * 浮层形态下，调主题时它盖住的正是你要看的东西 —— 调节点外观却
     * 看不见节点，只能"关掉看一眼再打开"。做成窗口之后两者并排，
     * 改一个值画布立刻跟着变，眼睛不用在两个状态之间来回切。
     *
     * 用 `row`（左右）而不是手机主力的 `col`：主题编辑器是一列设置项，
     * 它要的是**宽度**；而画布要的是高度。上下分栏会把两边都弄得难受。
     */
    build: () =>
      layout(
        split('row', 0.62, 'p-g|t', area('p-graph', 'graph'), area('p-theme', 'theme')),
        'p-graph',
      ),
  },
  {
    id: 'console',
    label: '只要输出',
    hint: '单窗口；排查问题时把屏幕全给诊断',
    build: () => layout(area('p-console', 'console'), 'p-console'),
  },
  {
    id: 'properties',
    label: '只要属性',
    hint: '单窗口；看模板信息与选中清单',
    build: () => layout(area('p-properties', 'properties'), 'p-properties'),
  },
]

/**
 * 结构指纹：方向 + **显示中的**编辑器类型，从左到右 / 从上到下。
 *
 * ⚠️ **不含比例**：拖一下分隔条就掉出「当前是哪个布局」是很糟的体验。
 * 导出去是为了让 `workspaces.ts` 用**同一把尺子**判定
 * 「当前在哪个工作区」—— 两处各写一份迟早会不一致。
 *
 * ⚠️ 用的是 `editorOf(node)`（**显示的那个**）而不是 `defaultEditor`：
 * 指纹回答的是「我这屏现在长什么样」——把输出窗口切成属性，
 * 那屏就是「节点图 + 属性」，工作区标签也该跟着改
 * （`verify:layout` 有两条断言钉着这个语义）。
 * `defaultEditor` 只在"当前那个装不上"时影响显示，所以它会通过 `editorOf` 体现出来。
 */
export function describeLayout(layout: LayoutState): string {
  return describe(layout.root)
}

function describe(node: AreaNode): string {
  if (node.kind === 'area') return editorOf(node)
  return `(${node.dir} ${describe(node.a)} ${describe(node.b)})`
}
